import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useNeedsReviewCount, needsReviewOrdersQueryString} from '../utils/useNeedsReviewCount';
import {makeStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';

// How long the pop-up stays before hiding on its own
const AUTO_HIDE_MS = 10000;

const useNotificationStyles = makeStyles((theme) => ({
    root: {
        top: 122,
        right: 34,
        '& > div': {
            backgroundColor: '#d32f2f',
            color: '#ffffff',
            margin: '-8px 8px 0 0',
            padding: '7px 8px 7px 20px',
        },
    },
    btnView: {
        color: '#ffffff',
        borderColor: '#ffffff',
    },
    btnClose: {
        color: '#ffffff',
        marginLeft: 4,
    },
}));

/**
 * An app-wide pop-up shown while Shopify orders sit in Needs Review
 * (parrot-media/portal/main#292). Dismissible; reappears when another order
 * enters the queue.
 *
 * @return {JSX.Element}
 * @constructor
 */
const NeedsReviewNotification = () => {

    const count = useNeedsReviewCount();
    // Highest count already announced. The pop-up opens only when the count
    // rises above it (a new arrival) and then auto-hides; the persistent
    // signal is the red Needs Review status label, not this pop-up.
    const [seenCount, setSeenCount] = useState(0);
    const classes = useNotificationStyles();

    useEffect(
        () => {
            // When the queue shrinks, lower the baseline so the next arrival
            // announces again
            if (count < seenCount) {
                setSeenCount(count);
            }
        }, [count, seenCount]
    );

    const open = count > seenCount;

    const handleClose = (event, reason) => {
        // Only the timer or the explicit buttons dismiss it
        if (reason === 'clickaway') {
            return;
        }
        setSeenCount(count);
    };

    const message = (count === 1)
        ? '1 Shopify Order needs review.'
        : `${count} Shopify Orders need review.`;

    const action = (
        <>
            <Button
                variant="outlined"
                size="small"
                component={Link}
                to={`/Order${needsReviewOrdersQueryString()}`}
                className={classes.btnView}
                onClick={() => setSeenCount(count)}
            >
                View Orders
            </Button>
            <IconButton
                aria-label="close"
                size="small"
                className={classes.btnClose}
                onClick={() => setSeenCount(count)}
            >
                <CloseIcon fontSize="small"/>
            </IconButton>
        </>
    );

    return (
        <Snackbar
            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            message={message}
            action={action}
            open={open}
            autoHideDuration={AUTO_HIDE_MS}
            onClose={handleClose}
            classes={{root: classes.root}}
        />
    );
}

export default NeedsReviewNotification;
